/**
 * Landing Page
 * Public marketing page for Google OAuth verification
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import './Landing.css';

const Landing = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="landing-header">
        <div className="landing-container">
          <div className="landing-logo">
            <img src="/fusionict-logo.png" alt="FusionICT" className="logo-image" />
            <span className="logo-text">Billing</span>
          </div>

          {/* Desktop Nav */}
          <nav className="landing-nav desktop-nav">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#about">About</a>
            {user ? (
              <Link to="/dashboard" className="btn btn-primary">Dashboard</Link>
            ) : (
              <Link to="/login" className="btn btn-primary">Sign In</Link>
            )}
          </nav>

          {/* Hamburger Button */}
          <button
            className="hamburger-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Nav */}
        <nav className={`landing-nav mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
          <a href="#features" onClick={closeMobileMenu}>Features</a>
          <a href="#pricing" onClick={closeMobileMenu}>Pricing</a>
          <a href="#about" onClick={closeMobileMenu}>About</a>
          {user ? (
            <Link to="/dashboard" className="btn btn-primary" onClick={closeMobileMenu}>Dashboard</Link>
          ) : (
            <Link to="/login" className="btn btn-primary" onClick={closeMobileMenu}>Sign In</Link>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-container">
          <div className="hero-content">
            <h1>Professional Billing & Invoicing for IT Service Providers</h1>
            <p className="hero-subtitle">
              Streamline your billing workflow with automated invoicing, time tracking,
              GCP cost management, and client portal access. Built for freelancers,
              agencies, and managed service providers.
            </p>
            <div className="hero-actions">
              <button onClick={handleGetStarted} className="btn btn-primary btn-large">
                Get Started Free
              </button>
              <a href="#features" className="btn btn-outline btn-large">
                Learn More
              </a>
            </div>
          </div>
          <div className="hero-image">
            <div className="hero-dashboard-preview">
              <div className="preview-header">
                <div className="preview-dots">
                  <span></span><span></span><span></span>
                </div>
              </div>
              <div className="preview-content">
                <div className="preview-stat">
                  <span className="stat-label">Monthly Revenue</span>
                  <span className="stat-value">$12,450</span>
                </div>
                <div className="preview-stat">
                  <span className="stat-label">Active Clients</span>
                  <span className="stat-value">24</span>
                </div>
                <div className="preview-stat">
                  <span className="stat-label">Pending Invoices</span>
                  <span className="stat-value">8</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="landing-features">
        <div className="landing-container">
          <h2>Everything You Need to Manage Your Billing</h2>
          <p className="section-subtitle">
            A complete billing solution designed specifically for IT service providers and agencies.
          </p>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <h3>Time Tracking</h3>
              <p>Track billable hours with an intuitive timer. Assign time entries to clients and projects for accurate invoicing.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
              </div>
              <h3>Invoice Generation</h3>
              <p>Create professional invoices from time entries or custom line items. Support for hourly, project-based, and retainer billing.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <h3>Payment Processing</h3>
              <p>Accept payments via Stripe. Track payment status, send reminders, and manage partial payments.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
                </svg>
              </div>
              <h3>GCP Cost Management</h3>
              <p>Automatically import Google Cloud Platform costs from BigQuery. Apply markups and bill clients for cloud infrastructure.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h3>Client Portal</h3>
              <p>Give clients secure access to view invoices, track costs, and manage payments through a branded portal.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                  <polyline points="17 6 23 6 23 12"/>
                </svg>
              </div>
              <h3>Recurring Billing</h3>
              <p>Set up subscriptions for retainer clients. Automatically generate and send invoices on your schedule.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="landing-pricing">
        <div className="landing-container">
          <h2>Simple, Transparent Pricing</h2>
          <p className="section-subtitle">
            Start free and scale as your business grows.
          </p>

          <div className="pricing-cards">
            <div className="pricing-card">
              <h3>Starter</h3>
              <div className="price">
                <span className="amount">$0</span>
                <span className="period">/month</span>
              </div>
              <ul className="pricing-features">
                <li>Up to 5 clients</li>
                <li>Time tracking</li>
                <li>Basic invoicing</li>
                <li>Email support</li>
              </ul>
              <button onClick={handleGetStarted} className="btn btn-outline">Get Started</button>
            </div>

            <div className="pricing-card featured">
              <div className="popular-badge">Most Popular</div>
              <h3>Professional</h3>
              <div className="price">
                <span className="amount">$29</span>
                <span className="period">/month</span>
              </div>
              <ul className="pricing-features">
                <li>Unlimited clients</li>
                <li>GCP cost integration</li>
                <li>Client portal</li>
                <li>Recurring billing</li>
                <li>Priority support</li>
              </ul>
              <button onClick={handleGetStarted} className="btn btn-primary">Get Started</button>
            </div>

            <div className="pricing-card">
              <h3>Enterprise</h3>
              <div className="price">
                <span className="amount">Custom</span>
              </div>
              <ul className="pricing-features">
                <li>Everything in Professional</li>
                <li>Multi-user access</li>
                <li>Custom integrations</li>
                <li>Dedicated support</li>
                <li>SLA guarantee</li>
              </ul>
              <a href="mailto:roseller@fusionict.com" className="btn btn-outline">Contact Us</a>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="landing-about">
        <div className="landing-container">
          <h2>About FusionICT Billing</h2>
          <p>
            FusionICT Billing is a comprehensive billing and invoicing platform designed specifically
            for IT service providers, managed service providers (MSPs), and technology agencies.
            Our platform helps you manage client relationships, track billable time, automate
            invoicing, and streamline payment collection.
          </p>
          <p>
            Whether you're a freelance developer, a small agency, or a growing MSP, FusionICT Billing
            provides the tools you need to professionally manage your billing operations and get paid
            faster.
          </p>
          <div className="about-stats">
            <div className="about-stat">
              <span className="stat-number">500+</span>
              <span className="stat-label">Invoices Generated</span>
            </div>
            <div className="about-stat">
              <span className="stat-number">$100K+</span>
              <span className="stat-label">Processed</span>
            </div>
            <div className="about-stat">
              <span className="stat-number">99.9%</span>
              <span className="stat-label">Uptime</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-cta">
        <div className="landing-container">
          <h2>Ready to Streamline Your Billing?</h2>
          <p>Join IT professionals who trust FusionICT Billing for their invoicing needs.</p>
          <button onClick={handleGetStarted} className="btn btn-primary btn-large">
            Start Free Today
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="landing-logo footer-logo-text">
                <span className="logo-text">FusionICT</span>
                <span className="logo-text-billing">Billing</span>
              </div>
              <p>Professional billing and invoicing for IT service providers.</p>
            </div>

            <div className="footer-links">
              <div className="footer-links-top">
                <div className="footer-column">
                  <h4>Product</h4>
                  <a href="#features">Features</a>
                  <a href="#pricing">Pricing</a>
                  <Link to="/login">Sign In</Link>
                </div>

                <div className="footer-column">
                  <h4>Contact</h4>
                  <a href="mailto:support@fusionict.com" className="contact-link">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    support@fusionict.com
                  </a>
                  <a href="https://wa.me/6596879852" target="_blank" rel="noopener noreferrer" className="contact-link">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    +65 9687 9852 (SG)
                  </a>
                  <a href="https://wa.me/639672345386" target="_blank" rel="noopener noreferrer" className="contact-link">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    +63 967 234 5386 (PH)
                  </a>
                  <div className="footer-socials">
                    <a href="https://www.facebook.com/FusionICTSolutions" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    </a>
                    <a href="https://www.linkedin.com/company/fusionictsolutions" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </a>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} FusionICT Billing. All rights reserved.</p>
            <div className="footer-bottom-links">
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms of Service</Link>
            </div>
            <p className="powered-by">
              Powered by <a href="https://fusionict.com" target="_blank" rel="noopener noreferrer">FusionICT</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
